// Microbe RTS — custom inline-SVG ICON SET for the DOM UI (HUD + evolve sheet).
//
// Replaces every platform emoji (⚡🧬☣🗡🛡🏹…) with hand-drawn, single-color SVGs that inherit the
// element's text color (currentColor) and scale to 1em. One visual language shared with the
// battlefield pictographs (render/icons.ts) so the same unit reads the same everywhere.

import type { UnitKind } from '../types.js';

type IconName =
  | 'energy' | 'amino' | 'toxin'
  | 'worker' | 'lancer' | 'phalanx' | 'stinger' | 'engulfer' | 'toxifer' | 'drone' | 'healer' | 'grazer'
  | 'flagella' | 'sun' | 'microbe' | 'egg' | 'lungs'
  | 'pause' | 'play' | 'plus' | 'target' | 'close' | 'check' | 'rally' | 'warn' | 'evolve'
  | 'lock' | 'unlock' | 'home';

const P: Record<IconName, string> = {
  energy: '<path d="M13 2 L4 14 h6 l-1 8 9-12 h-6 z" fill="currentColor" stroke="none"/>',
  amino: '<path d="M8 3 C16 8 8 16 16 21"/><path d="M16 3 C8 8 16 16 8 21"/><path d="M9.5 7h5 M9 12h6 M9.5 17h5"/>',
  toxin: '<circle cx="12" cy="12" r="2.2" fill="currentColor" stroke="none"/><circle cx="12" cy="7.2" r="3.3"/><circle cx="7.6" cy="15" r="3.3"/><circle cx="16.4" cy="15" r="3.3"/>',
  worker: '<path d="M4 8 C9 4 15 4 20 8"/><path d="M12 6 V20"/>',
  lancer: '<path d="M5 19 L19 5 M19 5 H13 M19 5 V11"/>',
  phalanx: '<path d="M12 3 L19 6 V12 C19 17 15 20 12 21 C9 20 5 17 5 12 V6 Z"/>',
  stinger: '<path d="M4 20 L20 4 M20 4 H14 M20 4 V10 M4 20 H9 M4 20 V15"/>',
  engulfer: '<path d="M18 5 A8 8 0 1 0 18 19 A5.4 5.4 0 1 1 18 5 Z" fill="currentColor" stroke="none"/>',
  toxifer: '<path d="M12 3 C18 12 16 20 12 20 C8 20 6 12 12 3 Z"/>',
  drone: '<path d="M2 12 C6 6 18 6 22 12 C18 18 6 18 2 12 Z"/><circle cx="12" cy="12" r="2.6" fill="currentColor" stroke="none"/>',
  healer: '<path d="M12 5 V19 M5 12 H19"/>',
  grazer: '<circle cx="12" cy="12" r="8"/><circle cx="12" cy="12" r="3" fill="currentColor" stroke="none"/>',
  flagella: '<path d="M13 11 a2.5 2.5 0 1 1 2.5 2.5 a5.5 5.5 0 1 1 -5.5 -5.5 a8.5 8.5 0 1 1 8.5 8.5"/>',
  sun: '<circle cx="12" cy="12" r="4"/><path d="M12 2v3 M12 19v3 M2 12h3 M19 12h3 M5 5l2 2 M17 17l2 2 M19 5l-2 2 M7 17l-2 2"/>',
  microbe: '<circle cx="12" cy="12" r="8"/><circle cx="10" cy="10" r="1.3" fill="currentColor" stroke="none"/><circle cx="14.5" cy="13" r="1.1" fill="currentColor" stroke="none"/><circle cx="11" cy="15" r="1" fill="currentColor" stroke="none"/>',
  egg: '<ellipse cx="12" cy="13" rx="6" ry="8"/>',
  lungs: '<path d="M12 4 V13 M12 8 C8 8 6 12 6 16 C6 19 9 20 10 18 C11 16 11 10 12 8 M12 8 C16 8 18 12 18 16 C18 19 15 20 14 18 C13 16 13 10 12 8"/>',
  pause: '<path d="M9 5 V19 M15 5 V19"/>',
  play: '<path d="M7 5 L19 12 L7 19 Z" fill="currentColor" stroke="none"/>',
  plus: '<path d="M12 5V19 M5 12H19"/>',
  target: '<circle cx="12" cy="12" r="7"/><path d="M12 2v4 M12 18v4 M2 12h4 M18 12h4"/><circle cx="12" cy="12" r="1.5" fill="currentColor" stroke="none"/>',
  close: '<path d="M6 6 L18 18 M18 6 L6 18"/>',
  check: '<path d="M5 13 L10 18 L19 6"/>',
  rally: '<path d="M6 3 V21 M6 4 H17 L14 8 L17 12 H6"/>',
  warn: '<path d="M12 3 L22 20 H2 Z"/><path d="M12 10 V14"/><circle cx="12" cy="17" r="0.6" fill="currentColor" stroke="none"/>',
  evolve: '<path d="M8 3 C16 8 8 16 16 21"/><path d="M16 3 C8 8 16 16 8 21"/><path d="M9.5 7h5 M9 12h6 M9.5 17h5"/>',
  lock: '<rect x="5" y="11" width="14" height="9" rx="2"/><path d="M8 11 V8 a4 4 0 0 1 8 0 v3"/>',
  unlock: '<rect x="5" y="11" width="14" height="9" rx="2"/><path d="M8 11 V8 a4 4 0 0 1 7.6 -1.7"/>',
  home: '<path d="M4 11 L12 4 L20 11"/><path d="M6 10 V19 a1 1 0 0 0 1 1 h10 a1 1 0 0 0 1 -1 V10"/>',
};

/** Raw inline-SVG markup for an icon (1em, currentColor). */
export function iconSvg(name: IconName): string {
  return (
    `<svg viewBox="0 0 24 24" width="1em" height="1em" fill="none" stroke="currentColor" ` +
    `stroke-width="2" stroke-linecap="round" stroke-linejoin="round" ` +
    `style="display:inline-block;vertical-align:-0.14em;flex:0 0 auto">${P[name]}</svg>`
  );
}

/** A <span> wrapping an icon (for appending into DOM). */
export function icon(name: IconName): HTMLSpanElement {
  const s = document.createElement('span');
  s.className = 'mi-icon';
  s.setAttribute('aria-hidden', 'true');
  s.innerHTML = iconSvg(name);
  return s;
}

export const UNIT_ICON: Record<UnitKind, IconName> = {
  lancer: 'lancer', phalanx: 'phalanx', stinger: 'stinger',
  engulfer: 'engulfer', toxifer: 'toxifer', drone: 'drone', healer: 'healer', grazer: 'grazer',
};

/** Semantic icon per mutation id (falls back to the dna/evolve mark). */
export const MUT_ICON: Record<string, IconName> = {
  t1_thick_wall: 'phalanx',
  t1_flagella: 'flagella',
  spec_t2: 'evolve',
  k_phototroph: 'sun',
  k_chemotroph: 'toxin',
  k_phagotroph: 'microbe',
  t2_broodplus: 'egg',
  spec_t3: 'evolve',
  k_aerobic: 'lungs',
};

export type { IconName };
